import * as VisuallyHidden from "@radix-ui/react-visually-hidden";
import * as Dialog from "@radix-ui/react-dialog";
import { type PropsWithChildren, useEffect, useRef, useState, useDeferredValue } from "react";
import { useParams } from "react-router-dom";
import { useSnapshot } from "valtio";
import { toast } from "sonner";
import { trim } from "validator";
import { faAt, faBriefcase, faPerson, faPhone } from "@fortawesome/free-solid-svg-icons";
import { Avatar } from "../../../../../components/avatar/Avatar";
import { getImageUrl } from "../../../../../utils/image";
import { useGetCompanies } from "../../../../../services/organtization/company/get-companies";
import { useSearchCompanies } from "../../../../../services/organtization/company/search-companies";
import { useLinkLeadCompany } from "../../../../../services/organtization/lead/link-lead-company";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerTitle,
} from "../../../../../components/drawer";
import { useGetArchivedLead, useGetLead, useUpdateLead } from "../../../../../services";
import { Button, Input } from "../../../../../components";
import { homeState } from "../../../../../state/home";
import { LeadRowCardSection } from "../card/LeadRowCardSection";
import EditorSkillTagButton from "../../../../../components/atoms/EditorSkillTagButton";

const formatRemote = (value: string) => {
  switch (value) {
    case "remote":
      return "Remote";
    case "remote_possible":
      return "Remote possible";
    case "no_remote":
      return "No remote";
    default:
      return value;
  }
};

export const AssignmentDrawer = ({ children }: PropsWithChildren) => {
  const { organizationId } = useParams();
  if (!organizationId) throw new Error("Organization ID is required");

  const { assignmentDrawerLeadId } = useSnapshot(homeState);
  const toastId = useRef<string | number>();
  const [showConfirm, setShowConfirm] = useState(false);

  const { data: activeLead } = useGetLead(
    {
      leadID: assignmentDrawerLeadId ?? "",
      organizationID: organizationId,
    },
    { enabled: !!assignmentDrawerLeadId },
  );

  const { data: archivedLead } = useGetArchivedLead(
    {
      leadID: assignmentDrawerLeadId ?? "",
      organizationID: organizationId,
    },
    { enabled: !!assignmentDrawerLeadId && !activeLead },
  );

  const lead = activeLead ?? archivedLead;

  const pad = lead?.parsedAssignmentData;

  const [localTitle, setLocalTitle] = useState("");
  const [localContactName, setLocalContactName] = useState("");
  const [localContactEmail, setLocalContactEmail] = useState("");
  const [localContactPhone, setLocalContactPhone] = useState("");
  const [localCompanyName, setLocalCompanyName] = useState("");
  const [selectedCompanyId, setSelectedCompanyId] = useState<string | null>(null);
  const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);

  const deferredSearch = useDeferredValue(localCompanyName);
  const { data: companies } = useGetCompanies(organizationId);
  const { data: searchResults } = useSearchCompanies(organizationId, deferredSearch);
  const linkCompany = useLinkLeadCompany(organizationId, assignmentDrawerLeadId ?? "");

  useEffect(() => {
    if (!searchResults || selectedCompanyId || !deferredSearch.trim()) return;
    const exact = searchResults.find(
      (c) => c.name.toLowerCase() === deferredSearch.toLowerCase(),
    );
    if (exact) {
      handleSelectCompany(exact.id, exact.name, exact.isOrgCompany);
    } else if (searchResults.length > 0) {
      setShowCompanyDropdown(true);
    }
  }, [searchResults]);

  const linkedCompanyId = (lead as any)?.companyId as string | undefined;

  useEffect(() => {
    setLocalTitle(lead?.title || pad?.title || "");
    setLocalContactName(lead?.contactPersonName ?? "");
    setLocalContactEmail(lead?.contactPersonEmail ?? "");
    setLocalContactPhone(lead?.contactPersonPhone ?? "");

    const linkedCompany = linkedCompanyId ? companies?.find((c) => c.id === linkedCompanyId) : undefined;
    setLocalCompanyName(linkedCompany?.name ?? "");
    setSelectedCompanyId(linkedCompanyId ?? null);
  }, [
    lead?.id,
    lead?.title,
    pad?.title,
    lead?.contactPersonName,
    lead?.contactPersonEmail,
    lead?.contactPersonPhone,
    linkedCompanyId,
    companies,
  ]);

  const updateLeadTitle = useUpdateLead({
    onSuccess: () => toast.success("Assignment title updated"),
    onError: () => toast.error("Failed to update title"),
  });

  const handleTitleBlur = () => {
    if (!lead?.id) return;
    updateLeadTitle.mutate({
      path: { organizationID: organizationId, leadID: lead.id },
      body: { ...lead, title: localTitle },
    });
  };

  const updateContactField = useUpdateLead({
    onError: () => toast.error("Failed to update contact information"),
  });

  const handleContactNameBlur = () => {
    if (!lead?.id) return;
    updateContactField.mutate({
      path: { organizationID: organizationId, leadID: lead.id },
      body: { ...lead, contactPersonName: localContactName },
    });
  };

  const handleContactEmailBlur = () => {
    if (!lead?.id) return;
    updateContactField.mutate({
      path: { organizationID: organizationId, leadID: lead.id },
      body: { ...lead, contactPersonEmail: localContactEmail },
    });
  };

  const handleContactPhoneBlur = () => {
    if (!lead?.id) return;
    updateContactField.mutate({
      path: { organizationID: organizationId, leadID: lead.id },
      body: { ...lead, contactPersonPhone: localContactPhone },
    });
  };

  const handleCompanyNameBlur = () => {
    if (!lead?.id || !localCompanyName.trim() || selectedCompanyId) return;
    linkCompany.mutate({ companyName: localCompanyName.trim() });
  };

  const handleSelectCompany = (companyId: string, name: string, isOrgCompany?: boolean) => {
    setSelectedCompanyId(companyId);
    setLocalCompanyName(name);
    setShowCompanyDropdown(false);
    if (isOrgCompany) {
      linkCompany.mutate({ companyName: name });
    } else {
      linkCompany.mutate({ companyId });
    }
  };

const updateLead = useUpdateLead({
    onMutate: () => {
      toastId.current = toast.loading(lead?.status === "archived" ? "Reopening lead..." : "Archiving lead...");
    },
    onSuccess: () => {
      toast.success(lead?.status === "archived" ? "Lead reopened" : "Lead archived", { id: toastId.current });
      homeState.assignmentDrawerLeadId = null;
    },
    onError: () => {
      toast.error("Something went wrong", { id: toastId.current });
    },
  });

  const handleClose = () => {
    homeState.assignmentDrawerLeadId = null;
  };

  const handleArchive = () => {
    if (!lead?.id) return;
    updateLead.mutate({
      path: { organizationID: organizationId, leadID: lead.id },
      body: { ...lead, status: "archived" },
    });
  };

  const handleUnarchive = () => {
    if (!lead?.id) return;
    updateLead.mutate({
      path: { organizationID: organizationId, leadID: lead.id },
      body: { ...lead, status: "" },
    });
  };

  return (
    <>
    <Drawer
      direction="right"
      open={!!assignmentDrawerLeadId}
      onClose={handleClose}
      shouldScaleBackground={false}
    >
      {children}

      <DrawerContent
        className="min-w-[480px] max-w-[520px]"
        direction="right"
        data-vaul-no-drag
      >
        <VisuallyHidden.Root>
          <DrawerTitle>Assignment details</DrawerTitle>
          <DrawerDescription>Full assignment information</DrawerDescription>
        </VisuallyHidden.Root>

        <div className="flex flex-col h-full overflow-y-auto">
          <div className="flex flex-row justify-between items-center gap-4 px-4 sm:px-5 py-4 bg-gray-1 border-b border-gray-2 rounded-tl-2xl">
            <div className="hidden sm:block flex-1 min-w-0">
              <Input
                leftIcon={faBriefcase}
                label="Assignment title"
                transformValue={trim}
                value={localTitle}
                onChange={(e) => setLocalTitle(e.target.value)}
                onBlur={handleTitleBlur}
              />
            </div>
            <div className="flex items-center gap-2">
              {lead && lead.status === "archived" ? (
                <Button
                  variant="secondary"
                  onClick={handleUnarchive}
                  disabled={updateLead.isPending}
                >
                  Unarchive
                </Button>
              ) : lead && (
                <Button
                  variant="secondary"
                  onClick={() => setShowConfirm(true)}
                  disabled={updateLead.isPending}
                >
                  Archive
                </Button>
              )}
              <Button variant="primary" onClick={handleClose}>
                Close
              </Button>
            </div>
          </div>

          <div className="flex flex-col gap-3 px-6 py-4 border-b border-gray-2">
            <LeadRowCardSection title="Company">
              <div className="relative">
                {localCompanyName && (
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 w-7 h-7 rounded-md overflow-hidden shrink-0 pointer-events-none z-10">
                    <Avatar
                      imageUrl={
                        selectedCompanyId
                          ? (companies?.find((c) => c.id === selectedCompanyId)?.logoId
                              ? getImageUrl(companies.find((c) => c.id === selectedCompanyId)!.logoId!)
                              : undefined)
                          : undefined
                      }
                      alt={localCompanyName}
                      fallbackClassName="text-xs"
                    />
                  </div>
                )}
                <input
                  type="text"
                  placeholder="Search or enter company name…"
                  value={localCompanyName}
                  onFocus={() => setShowCompanyDropdown(true)}
                  onBlur={() => {
                    setTimeout(() => setShowCompanyDropdown(false), 150);
                    handleCompanyNameBlur();
                  }}
                  onChange={(e) => {
                    setLocalCompanyName(e.target.value);
                    setSelectedCompanyId(null);
                  }}
                  className={`w-full ${localCompanyName ? "pl-12" : "pl-4"} pr-4 py-4 text-sm border border-gray-4 rounded-2xl outline-none focus:border-gray-5 bg-transparent`}
                />
                {showCompanyDropdown && (
                  (deferredSearch.trim().length > 0 ? searchResults?.length : companies?.length) ? (
                    <div className="absolute top-full left-0 right-0 mt-1 border border-gray-3 rounded-2xl bg-white shadow-sm overflow-hidden z-10">
                      <div className="flex flex-col max-h-56 overflow-y-auto p-2">
                        {deferredSearch.trim().length > 0 ? (
                          searchResults?.map((company) => (
                            <button
                              key={company.id}
                              type="button"
                              onMouseDown={() => handleSelectCompany(company.id, company.name, company.isOrgCompany)}
                              className="flex items-center justify-between gap-2 px-3 py-2 text-sm rounded-xl hover:bg-gray-1 transition-colors text-left"
                            >
                              <span className="truncate">{company.name}</span>
                              {!company.isOrgCompany && (
                                <span className="text-xs text-gray-5 shrink-0">+ add</span>
                              )}
                            </button>
                          ))
                        ) : (
                          companies?.map((company) => (
                            <button
                              key={company.id}
                              type="button"
                              onMouseDown={() => handleSelectCompany(company.id, company.name)}
                              className="flex items-center gap-2 px-3 py-2 text-sm rounded-xl hover:bg-gray-1 transition-colors text-left"
                            >
                              {company.name}
                            </button>
                          ))
                        )}
                      </div>
                    </div>
                  ) : null
                )}
              </div>
            </LeadRowCardSection>
            <LeadRowCardSection title="Contact information">
              <Input
                leftIcon={faPerson}
                label="Contact name"
                value={localContactName}
                onChange={(e) => setLocalContactName(e.target.value)}
                onBlur={handleContactNameBlur}
                transformValue={trim}
              />
              <Input
                leftIcon={faAt}
                label="E-mail"
                value={localContactEmail}
                onChange={(e) => setLocalContactEmail(e.target.value)}
                onBlur={handleContactEmailBlur}
                transformValue={trim}
                type="email"
              />
              <Input
                leftIcon={faPhone}
                label="Phone number"
                value={localContactPhone}
                onChange={(e) => setLocalContactPhone(e.target.value)}
                onBlur={handleContactPhoneBlur}
                transformValue={trim}
                type="tel"
              />
            </LeadRowCardSection>
          </div>

          {pad && (
            <div className="flex flex-col gap-3 px-6 py-4">
              {pad.seniorityLevel && pad.seniorityLevel !== "" && (
                <LeadRowCardSection title="Seniority" secondarySection className="text-sm">
                  <ul className="list-disc list-inside text-sm px-4">
                    <li>{pad.seniorityLevel}</li>
                    {pad.yearsOfExperience && pad.yearsOfExperience !== "" && (
                      <li>{pad.yearsOfExperience} years of experience</li>
                    )}
                  </ul>
                </LeadRowCardSection>
              )}

              {pad.skills?.technical_required && pad.skills.technical_required.length > 0 && (
                <LeadRowCardSection title="Required technical skills" secondarySection>
                  <div className="flex flex-row flex-wrap gap-2 px-4">
                    {pad.skills.technical_required.map((skill, i) => (
                      <EditorSkillTagButton
                        key={i}
                        text={skill}
                        handleClick={() => {}}
                        displayInfoOnHover
                      />
                    ))}
                  </div>
                </LeadRowCardSection>
              )}

              {pad.skills?.technical_optional && pad.skills.technical_optional.length > 0 && (
                <LeadRowCardSection title="Optional technical skills" secondarySection>
                  <div className="flex flex-row flex-wrap gap-2 px-4">
                    {pad.skills.technical_optional.map((skill, i) => (
                      <EditorSkillTagButton
                        key={i}
                        text={skill}
                        handleClick={() => {}}
                        displayInfoOnHover
                      />
                    ))}
                  </div>
                </LeadRowCardSection>
              )}

              {pad.skills?.soft_skills && pad.skills.soft_skills.length > 0 && (
                <LeadRowCardSection title="Soft skills" secondarySection>
                  <ul className="list-disc list-inside text-sm px-4">
                    {pad.skills.soft_skills.map((s) => (
                      <li key={s}>{s}</li>
                    ))}
                  </ul>
                </LeadRowCardSection>
              )}

              {pad.skills?.domain_expertise && pad.skills.domain_expertise.length > 0 && (
                <LeadRowCardSection title="Domain expertise" secondarySection>
                  <ul className="list-disc list-inside text-sm px-4">
                    {pad.skills.domain_expertise.map((s) => (
                      <li key={s}>{s}</li>
                    ))}
                  </ul>
                </LeadRowCardSection>
              )}

              {pad.workplace && pad.workplace !== "" && (
                <LeadRowCardSection title="Workplace" secondarySection className="text-sm">
                  <ul className="list-disc list-inside text-sm px-4">
                    <li>{pad.workplace}</li>
                    {pad.workplaceInfo && <li>{pad.workplaceInfo}</li>}
                  </ul>
                </LeadRowCardSection>
              )}

              {pad.sectorSpace && (
                <LeadRowCardSection title="Sector" secondarySection>
                  <ul className="list-disc list-inside text-sm px-4">
                    <li>
                      {pad.sectorSpace.charAt(0).toUpperCase() + pad.sectorSpace.slice(1)}
                    </li>
                  </ul>
                </LeadRowCardSection>
              )}

              {pad.industry && pad.industry !== "" && (
                <LeadRowCardSection title="Industry" secondarySection>
                  <ul className="list-disc list-inside text-sm px-4">
                    <li>{pad.industry}</li>
                  </ul>
                </LeadRowCardSection>
              )}

              {pad.profile && pad.profile !== "" && (
                <LeadRowCardSection title="Profile" secondarySection className="text-sm">
                  <ul className="list-disc list-inside text-sm px-4">
                    <li>{pad.profile}</li>
                  </ul>
                </LeadRowCardSection>
              )}

              {pad.businessContext && pad.businessContext !== "" && (
                <LeadRowCardSection title="Business context" secondarySection className="text-sm">
                  <ul className="list-disc list-inside text-sm px-4">
                    <li>{pad.businessContext}</li>
                  </ul>
                </LeadRowCardSection>
              )}

              {pad.responsibilities && pad.responsibilities.length > 0 && (
                <LeadRowCardSection title="Responsibilities" secondarySection>
                  <ul className="list-disc list-inside text-sm px-4">
                    {pad.responsibilities.map((r) => (
                      <li key={r}>{r}</li>
                    ))}
                  </ul>
                </LeadRowCardSection>
              )}

              {pad.keySuccessFactors && pad.keySuccessFactors.length > 0 && (
                <LeadRowCardSection title="Key success factors" secondarySection>
                  <ul className="list-disc list-inside text-sm px-4">
                    {pad.keySuccessFactors.map((f) => (
                      <li key={f}>{f}</li>
                    ))}
                  </ul>
                </LeadRowCardSection>
              )}

              {pad.mustHaveQualifiers && pad.mustHaveQualifiers.length > 0 && (
                <LeadRowCardSection title="Must have" secondarySection>
                  <ul className="list-disc list-inside text-sm px-4">
                    {pad.mustHaveQualifiers.map((q) => (
                      <li key={q}>{q}</li>
                    ))}
                  </ul>
                </LeadRowCardSection>
              )}

              {pad.niceToHaveQualifiers && pad.niceToHaveQualifiers.length > 0 && (
                <LeadRowCardSection title="Nice to have" secondarySection>
                  <ul className="list-disc list-inside text-sm px-4">
                    {pad.niceToHaveQualifiers.map((q) => (
                      <li key={q}>{q}</li>
                    ))}
                  </ul>
                </LeadRowCardSection>
              )}

              {pad.location && pad.location.length > 0 && (
                <LeadRowCardSection title="Location" secondarySection>
                  <ul className="list-disc list-inside text-sm px-4">
                    {pad.location.map((l) => (
                      <li key={l}>{l}</li>
                    ))}
                  </ul>
                </LeadRowCardSection>
              )}

              {pad.remote && (
                <LeadRowCardSection title="Remote" secondarySection>
                  <ul className="list-disc list-inside text-sm px-4">
                    <li>{formatRemote(pad.remote)}</li>
                  </ul>
                </LeadRowCardSection>
              )}

              {pad.date && pad.date !== "" && (
                <LeadRowCardSection title="Date" secondarySection className="text-sm">
                  <ul className="list-disc list-inside text-sm px-4">
                    <li>{pad.date}</li>
                  </ul>
                </LeadRowCardSection>
              )}
            </div>
          )}
        </div>
      <Dialog.Root open={showConfirm} onOpenChange={setShowConfirm}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-50 z-50" />
          <Dialog.Content className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-2xl p-6 shadow-lg z-50 max-w-sm w-full mx-4">
            <Dialog.Title className="text-lg font-bold mb-2">Archive lead?</Dialog.Title>
            <Dialog.Description className="text-sm text-gray-500 mb-6">
              By archiving a Lead you close all Profile Versions added to it. You can find archived leads below your active leads list and unarchive it.
            </Dialog.Description>
            <div className="flex justify-end gap-3">
              <Button variant="secondary" onClick={() => setShowConfirm(false)}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={() => {
                  setShowConfirm(false);
                  handleArchive();
                }}
                disabled={updateLead.isPending}
              >
                Archive
              </Button>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
      </DrawerContent>
    </Drawer>
    </>
  );
};
